import { SSR_QUERY } from '@/router/query-params'

const STORAGE_KEY = 'pg_devtools'

export type DevtoolsUiState = {
    open: boolean
    tab: string
    side: 'left' | 'right'
    launcherTopShare: number
    width: number
    height: number
}

function createUiState(): DevtoolsUiState {
    return {
        open: true,
        tab: '',
        side: 'right',
        launcherTopShare: 0.7,
        width: 440,
        height: Math.round(window.innerHeight * 0.6),
    }
}

// Storage throws in sandboxed iframes and some private modes, and may hold anything; DevTools then start over
function accessStorage<T>(access: (storage: Storage) => T): T | undefined {
    try {
        return access(window.localStorage)
    } catch {
        return undefined
    }
}

function readUiState(): DevtoolsUiState | undefined {
    const saved = accessStorage(
        (storage) => JSON.parse(storage.getItem(STORAGE_KEY) || 'null') as Partial<DevtoolsUiState> | null,
    )
    return saved ? { ...createUiState(), ...saved } : undefined
}

export function writeUiState(state: DevtoolsUiState) {
    accessStorage((storage) => storage.setItem(STORAGE_KEY, JSON.stringify(state)))
}

export function clearUiState() {
    accessStorage((storage) => storage.removeItem(STORAGE_KEY))
}

function readQuery(): string | null {
    for (const win of new Set([window, window.top])) {
        try {
            const value = new URLSearchParams(win?.location.search).get(SSR_QUERY.DEVTOOLS)
            if (value !== null) {
                return value
            }
        } catch {
            // cross-origin top window: only our own frame's URL is readable
        }
    }
    return null
}

export function resolveUiState(): DevtoolsUiState | undefined {
    const query = readQuery()
    if (query === '0') {
        clearUiState()
        return undefined
    }
    if (query === null) {
        return readUiState()
    }
    const state = readUiState() || createUiState()
    writeUiState(state)
    return state
}
